
import React, { useState, useEffect } from 'react';
import { Lead } from '@/components/leads/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Edit3, Save, X, Check, Calendar as CalendarIcon } from 'lucide-react'; // Added CalendarIcon for consistency if needed, though DatePickerField has its own
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from '@/lib/utils';
import { DatePickerField } from '@/components/candidates/forms/fields/DatePickerField';
import { format, parseISO, isValid, toDate } from 'date-fns';

interface EditableDetailItemProps {
  icon?: React.ReactNode;
  label: string;
  value?: string | number | null | Date | React.ReactNode; // Allow Date for value
  fieldName: keyof Lead;
  leadId: string;
  onSave: (leadId: string, fieldName: keyof Lead, value: any) => Promise<void>;
  inputType?: 'text' | 'textarea' | 'number' | 'select' | 'date'; // Added 'date'
  selectOptions?: Record<string, string>;
  className?: string;
  displayFormatter?: (value: any, fieldName: keyof Lead, selectOptions?: Record<string, string>) => React.ReactNode;
  isLoading?: boolean;
}

export const EditableDetailItem: React.FC<EditableDetailItemProps> = ({
  icon,
  label,
  value,
  fieldName,
  leadId,
  onSave,
  inputType = 'text',
  selectOptions,
  className,
  displayFormatter,
  isLoading = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  // currentValue can be string, number, Date, null, or undefined
  const [currentValue, setCurrentValue] = useState<any>(value);

  useEffect(() => {
    // Initialize currentValue based on inputType and value prop
    if (inputType === 'date') {
      if (value instanceof Date && isValid(value)) {
        setCurrentValue(value);
      } else if (typeof value === 'string') {
        const parsed = parseISO(value);
        setCurrentValue(isValid(parsed) ? parsed : null);
      } else if (value === null || value === undefined) {
        setCurrentValue(null);
      } else {
         // Fallback for other unexpected value types for date input
        setCurrentValue(value);
      }
    } else {
      setCurrentValue(value);
    }
  }, [value, inputType]);

  const handleEdit = () => setIsEditing(true);
  const handleCancel = () => {
    setIsEditing(false);
    // Reset to original value, converting if necessary for date type
    if (inputType === 'date') {
      if (value instanceof Date && isValid(value)) {
        setCurrentValue(value);
      } else if (typeof value === 'string') {
        const parsed = parseISO(value);
        setCurrentValue(isValid(parsed) ? parsed : null);
      } else {
        setCurrentValue(null);
      }
    } else {
      setCurrentValue(value);
    }
  };

  const handleSave = async () => {
    try {
      let valueToSave = currentValue;
      if (inputType === 'date') {
        if (currentValue instanceof Date && isValid(currentValue)) {
          valueToSave = currentValue.toISOString();
        } else if (currentValue === null || currentValue === undefined) {
          // Allow clearing the date
          valueToSave = null;
        } else {
          // If currentValue is not a valid date (e.g. invalid string input somehow got here),
          // perhaps default to null or handle as an error. For now, send as is or null.
          console.warn(`Attempting to save invalid date for ${fieldName}:`, currentValue);
          valueToSave = null; 
        }
      }
      await onSave(leadId, fieldName, valueToSave);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to save:", error);
      // Error toast is handled by the mutation hook
    }
  };

  const renderInput = () => {
    switch (inputType) {
      case 'textarea':
        return (
          <Textarea
            value={typeof currentValue === 'string' ? currentValue : ''}
            onChange={(e) => setCurrentValue(e.target.value)}
            className="mt-1 min-h-[80px]"
            disabled={isLoading}
          />
        );
      case 'select':
        return (
          <Select
            value={typeof currentValue === 'string' ? currentValue : ''}
            onValueChange={(val) => setCurrentValue(val)}
            disabled={isLoading}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder={`Select ${label.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {selectOptions && Object.entries(selectOptions).map(([optVal, optLabel]) => (
                <SelectItem key={optVal} value={optVal}>
                  {optLabel}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'number':
        return (
          <Input
            type="number"
            value={currentValue === null || currentValue === undefined ? '' : String(currentValue)}
            onChange={(e) => setCurrentValue(e.target.value === '' ? null : Number(e.target.value))}
            className="mt-1"
            disabled={isLoading}
          />
        );
      case 'date':
        // Ensure currentValue passed to DatePickerField is a Date object or undefined
        let dateForPicker: Date | undefined = undefined;
        if (currentValue instanceof Date && isValid(currentValue)) {
          dateForPicker = currentValue;
        } else if (typeof currentValue === 'string') {
            const parsed = parseISO(currentValue);
            if (isValid(parsed)) dateForPicker = parsed;
        }
        // If currentValue is null, dateForPicker remains undefined, which is correct for DatePickerField

        return (
          <DatePickerField
            label="" // Label is part of EditableDetailItem itself
            value={dateForPicker}
            onChange={(dateOption) => setCurrentValue(dateOption === undefined ? null : dateOption)} // Store Date or null
          />
        );
      case 'text':
      default:
        return (
          <Input
            type="text"
            value={typeof currentValue === 'string' ? currentValue : ''}
            onChange={(e) => setCurrentValue(e.target.value)}
            className="mt-1"
            disabled={isLoading}
          />
        );
    }
  };
  
  let finalDisplayValue: React.ReactNode;

  if (displayFormatter) {
    finalDisplayValue = displayFormatter(value, fieldName, selectOptions);
  } else if (inputType === 'date') {
    if (value instanceof Date && isValid(value)) {
      finalDisplayValue = format(value, 'PPP');
    } else if (typeof value === 'string' && value.trim() !== '') {
      const parsedDate = parseISO(value);
      if (isValid(parsedDate)) {
        finalDisplayValue = format(parsedDate, 'PPP');
      } else {
        finalDisplayValue = value; // Show original string if not parsable but not empty
      }
    } else { // value is null, undefined, or empty string
      finalDisplayValue = 'N/A';
    }
  } else {
    finalDisplayValue = (value === undefined || value === null || (typeof value === 'string' && value.trim() === '') ? 'N/A' : String(value));
  }

  return (
    <div className={cn("flex items-start space-x-3 py-2 group", className)}>
      {icon && <div className="text-muted-foreground mt-1">{icon}</div>}
      <div className="flex-1">
        <div className="flex justify-between items-center">
          <p className="text-sm font-medium text-gray-500">{label}</p>
          {!isEditing && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleEdit}
              className="opacity-0 group-hover:opacity-100 transition-opacity"
              disabled={isLoading}
            >
              <Edit3 size={14} className="mr-1" /> Edit
            </Button>
          )}
        </div>
        {isEditing ? (
          <div className="mt-1">
            {renderInput()}
            <div className="flex justify-end space-x-2 mt-2">
              <Button variant="outline" size="sm" onClick={handleCancel} disabled={isLoading}>
                <X size={14} className="mr-1" /> Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={isLoading}>
                {isLoading ? <Check size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-900 break-words mt-0.5">
             {typeof finalDisplayValue === 'string' && finalDisplayValue.length > 100 && !React.isValidElement(finalDisplayValue) ? 
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>{finalDisplayValue.substring(0,100)}...</span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs whitespace-pre-wrap">{finalDisplayValue}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              : finalDisplayValue
            }
          </div>
        )}
      </div>
    </div>
  );
};
